import { useState, useEffect } from 'react';
import { CreationResponse, CreationStatus } from '../../api/client';
// import { messages } from '../../messages'; // Keep for message lists -> No longer needed
import { useTranslation } from 'react-i18next'; // Import useTranslation

interface ProcessingStateProps {
  creation: CreationResponse;
  strategyType: string;
  onStatusChange?: (updatedCreation: CreationResponse) => void;
}

// Processing time in milliseconds for different strategies
const PROCESSING_TIMES: Record<string, number> = {
  'kovrification': 3 * 60 * 1000, // 3 minutes for kovrification
  'best_moments': 10 * 60 * 1000, // 10 minutes for best moments
};

// Fun and informative processing messages
const PROCESSING_MESSAGES = [
  // Informative messages
  "Analyzing video content...",
  "Extracting audio tracks...",
  "Generating captions from audio...",
  "Synchronizing captions with video...",
  "Applying caption styling...",
  "Optimizing video for vertical format...",
  "Enhancing video quality...",
  "Adjusting color balance...",
  "Applying visual effects...",
  "Rendering final output...",
  "Preparing for download...",
  
  // Best moments specific messages
  "Identifying engaging segments...",
  "Finding narrative highlights...",
  "Analyzing audio for key moments...",
  "Segmenting content into best parts...",
  "Detecting emotional peaks in content...",
  "Evaluating audience engagement points...",
  "Prioritizing the most impactful moments...",
  "Cutting video into best segments...",
  
  // Fun messages
  "Teaching AI to understand your accent...",
  "Convincing pixels to rearrange themselves...",
  "Feeding hamsters that power our servers...",
  "Bribing the algorithm to work faster...",
  "Translating your content to robot language and back...",
  "Applying magic internet dust...",
  "Consulting with digital content experts (cats on keyboards)...",
  "Calculating the meaning of life, the universe, and your video...",
  "Polishing each pixel individually...",
  "Negotiating with stubborn frames...",
  "Hunting for the perfect font...",
  "Asking ChatGPT for video editing advice...",
  "Summoning the spirit of Stanley Kubrick...",
  "Brewing coffee for the rendering engine...",
  "Convincing AI not to add cats to your video...",
  "Applying secret sauce to your content...",
  "Performing digital alchemy...",
  "Sprinkling fairy dust on your creation...",
  "Consulting the ancient scrolls of video processing...",
  "Sending your video to space and back for cosmic enhancement...",
];

// Messages for when processing takes longer than expected
const EXTENDED_PROCESSING_MESSAGES = [
  "This is taking a bit longer than expected...",
  "Your video is complex, giving it extra attention...",
  "Almost there, just adding some final touches...",
  "Processing the fine details of your masterpiece...",
  "Our AI is working overtime on your creation...",
  "Your video is so good, it's taking extra time to perfect...",
  "Hang tight, we're in the final stretch...",
  "The hamsters powering our servers needed a water break...",
  "Your video is getting VIP treatment...",
  "Just a bit longer for exceptional quality...",
];

// Early completion messages
const EARLY_COMPLETION_MESSAGES = [
  "Finished ahead of schedule!",
  "That was quick! All done!",
  "Processing complete earlier than expected!",
  "Your video is ready sooner than we thought!",
  "Well that was fast! All done!",
];

export function ProcessingState({ creation, strategyType, onStatusChange }: ProcessingStateProps) {
  const { t } = useTranslation(); // Initialize translation hook
  const [progress, setProgress] = useState(0);
  const [messageIndex, setMessageIndex] = useState(0);
  const [currentMessage, setCurrentMessage] = useState(PROCESSING_MESSAGES[0]);
  const [isExtendedProcessing, setIsExtendedProcessing] = useState(false);
  const [extendedMessageIndex, setExtendedMessageIndex] = useState(0);
  const [isEarlyCompletion, setIsEarlyCompletion] = useState(false);
  const [earlyCompletionMessage, setEarlyCompletionMessage] = useState(EARLY_COMPLETION_MESSAGES[0]);
  
  // Check for status changes that would trigger early completion
  useEffect(() => {
    // If status is COMPLETED and we're not at 100% yet, show early completion
    if ((creation.status === CreationStatus.COMPLETED || creation.status === CreationStatus.FAILED) && 
        progress < 99 && 
        !isEarlyCompletion && 
        creation.status === CreationStatus.COMPLETED) {
      
      setIsEarlyCompletion(true);
      // Pick a random early completion message
      const randomIndex = Math.floor(Math.random() * EARLY_COMPLETION_MESSAGES.length);
      setEarlyCompletionMessage(EARLY_COMPLETION_MESSAGES[randomIndex]);
      
      // Animate to 100% quickly
      let currentProgress = progress;
      const progressInterval = setInterval(() => {
        currentProgress += 5;
        if (currentProgress >= 100) {
          clearInterval(progressInterval);
          currentProgress = 100;
          
          // After a short delay, notify the parent to update the view
          setTimeout(() => {
            if (onStatusChange) {
              onStatusChange(creation);
            }
          }, 1500); // Show the 100% state for 1.5 seconds
        }
        setProgress(currentProgress);
      }, 50); // Update every 50ms for a smooth animation
    }
  }, [creation.status, progress, isEarlyCompletion, onStatusChange, creation]);
  
  useEffect(() => {
    // Don't update progress if we're in early completion mode
    if (isEarlyCompletion) return;
    
    // Calculate progress based on updated_at time
    const calculateProgress = () => {
      const updatedAt = new Date(creation.updated_at);
      const processingTime = PROCESSING_TIMES[strategyType] || PROCESSING_TIMES['kovrification'];
      const estimatedCompletionTime = new Date(updatedAt.getTime() + processingTime);
      const now = new Date();
      
      // Calculate elapsed time since update
      const elapsedTime = now.getTime() - updatedAt.getTime();
      
      // Check if we've exceeded the estimated processing time
      if (elapsedTime > processingTime) {
        setIsExtendedProcessing(true);
        return {
          progress: 99,
          isExtended: true,
          remainingMinutes: 0,
          remainingSeconds: 0
        };
      }
      
      // Calculate progress percentage (capped at 99% until complete)
      const calculatedProgress = Math.min(99, Math.floor((elapsedTime / processingTime) * 100));
      
      setProgress(calculatedProgress);
      
      // Calculate estimated time remaining in minutes and seconds
      const remainingTime = Math.max(0, estimatedCompletionTime.getTime() - now.getTime());
      const remainingMinutes = Math.floor(remainingTime / 60000);
      const remainingSeconds = Math.floor((remainingTime % 60000) / 1000);
      
      return {
        progress: calculatedProgress,
        isExtended: false,
        remainingMinutes,
        remainingSeconds
      };
    };
    
    // Initial calculation
    calculateProgress();
    
    // Update progress every second
    const progressInterval = setInterval(() => {
      const { progress, isExtended } = calculateProgress();
      setProgress(progress);
      
      // If we've entered extended processing, we might want to change the message set
      if (isExtended && !isExtendedProcessing) {
        setIsExtendedProcessing(true);
        setExtendedMessageIndex(0);
        setCurrentMessage(EXTENDED_PROCESSING_MESSAGES[0]);
      }
    }, 1000);
    
    // Change message every 15 seconds
    const messageInterval = setInterval(() => {
      if (isExtendedProcessing) {
        // Use extended processing messages
        let newIndex;
        do {
          newIndex = Math.floor(Math.random() * EXTENDED_PROCESSING_MESSAGES.length);
        } while (newIndex === extendedMessageIndex && EXTENDED_PROCESSING_MESSAGES.length > 1);
        
        setExtendedMessageIndex(newIndex);
        setCurrentMessage(EXTENDED_PROCESSING_MESSAGES[newIndex]);
      } else {
        // Use regular processing messages
        let newIndex;
        do {
          newIndex = Math.floor(Math.random() * PROCESSING_MESSAGES.length);
        } while (newIndex === messageIndex && PROCESSING_MESSAGES.length > 1);
        
        setMessageIndex(newIndex);
        setCurrentMessage(PROCESSING_MESSAGES[newIndex]);
      }
    }, 15000);
    
    return () => {
      clearInterval(progressInterval);
      clearInterval(messageInterval);
    };
  }, [creation.updated_at, strategyType, messageIndex, extendedMessageIndex, isExtendedProcessing, isEarlyCompletion]);
  
  // Format the estimated completion time
  const formatEstimatedTime = () => {
    if (isEarlyCompletion) {
      return "Finishing up...";
    }
    
    if (isExtendedProcessing) {
      return "Almost there...";
    }
    
    const updatedAt = new Date(creation.updated_at);
    const processingTime = PROCESSING_TIMES[strategyType] || PROCESSING_TIMES['kovrification'];
    const estimatedCompletionTime = new Date(updatedAt.getTime() + processingTime);
    const now = new Date();
    
    // Calculate remaining time in minutes and seconds
    const remainingTime = Math.max(0, estimatedCompletionTime.getTime() - now.getTime());
    const remainingMinutes = Math.floor(remainingTime / 60000);
    const remainingSeconds = Math.floor((remainingTime % 60000) / 1000);
    
    if (remainingMinutes <= 0 && remainingSeconds <= 0) {
      return "Almost done...";
    }
    
    return `Estimated time: ${remainingMinutes}m ${remainingSeconds}s`;
  };
  
  return (
    <div className="bg-surface-interactive dark:bg-surface-interactive-dark border border-interactive dark:border-interactive-dark rounded-2xl p-6 text-center shadow-interactive">
      <div className="flex flex-col items-center">
        {/* Circular progress indicator - only show when not in extended processing */}
        {!isExtendedProcessing || isEarlyCompletion ? (
          <div className="relative w-32 h-32 mb-4">
            <svg className="w-full h-full" viewBox="0 0 100 100">
              {/* Background circle */}
              <circle
                className="text-progress-track dark:text-progress-track-dark stroke-current"
                strokeWidth="8"
                cx="50"
                cy="50"
                r="40"
                fill="transparent"
              />
              {/* Progress circle */}
              <circle
                className="text-primary-accent stroke-current"
                strokeWidth="8"
                strokeLinecap="round"
                cx="50"
                cy="50"
                r="40"
                fill="transparent"
                strokeDasharray="251.2"
                strokeDashoffset={251.2 - (251.2 * progress) / 100}
                transform="rotate(-90 50 50)"
              />
            </svg>
            
            {/* Spinner animation around the circle */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-40 h-40 border-4 border-transparent border-t-primary-accent-alpha-30 rounded-full animate-spin"></div>
            </div>
            
            {/* Percentage text - positioned absolutely for better theme support */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-2xl font-bold text-content-strong">
                {progress}%
              </span>
            </div>
          </div>
        ) : (
          /* Extended processing spinner */
          <div className="relative w-32 h-32 mb-4 flex items-center justify-center">
            <div className="w-20 h-20 border-4 border-primary-accent-alpha-30 border-t-primary-accent rounded-full animate-spin"></div>
          </div>
        )}
        
        <h3 className="text-xl font-medium text-content-strong mb-2">
          {isEarlyCompletion 
            ? earlyCompletionMessage
            : isExtendedProcessing 
              ? "Still Processing..." 
              : "Processing Your Creation"}
        </h3>
        
        <p className="text-theme-hint mb-4">
          {formatEstimatedTime()}
        </p>
        
        {/* Processing message with loading animation - smaller dots */}
        <div className="flex items-center gap-2 text-content-emphasis bg-primary-accent-alpha-10 px-4 py-2 rounded-full">
          <div className="flex space-x-1">
            <div className="w-1.5 h-1.5 bg-primary-accent rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-1.5 h-1.5 bg-primary-accent rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-1.5 h-1.5 bg-primary-accent rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
          <span>{isEarlyCompletion ? t('processingState.finalizingCreation') : currentMessage}</span>
        </div>
      </div>
    </div>
  );
} 